import { RunType } from 'Interfaces/ReferenceInterfaces/RunTypeInterface';
import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { runTypeScreenServices } from 'services/ReferenceScreenServices/runTypeServices';

export class RunTypeStore {
  runTypes: RunType[] = [];
  isLoading: boolean = false;
  pagination = {
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    hasNext: false,
    hasPrevious: false
  };
  selectedRunType: RunType | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  fetchRunTypes = async (page = 1, pageSize = 10000) => {
    this.isLoading = true;
    try {
      const response = await runTypeScreenServices.getAllRunTypes(page, pageSize);
      runInAction(() => {
        this.runTypes = response.data;
        this.pagination = {
          currentPage: response.pagination.current_page,
          totalPages: response.pagination.total_pages,
          totalItems: response.pagination.total_items,
          hasNext: response.pagination.has_next,
          hasPrevious: response.pagination.has_previous
        };
      });
    } catch (error) {
      toast.error('Error fetching run types');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  createRunType = async (runTypeData: Partial<RunType>) => {
    this.isLoading = true;
    try {
      const response = await runTypeScreenServices.createRunType(runTypeData);
      if (response.status) {
        toast.success('Run type created successfully');
        this.fetchRunTypes(); // Refresh the list
      } else {
        toast.error(response.message || 'Failed to create run type');
      }
    } catch (error) {
      toast.error('Error creating run type');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  updateRunType = async (id: number, runTypeData: Partial<RunType>) => {
    this.isLoading = true;
    try {
      const response = await runTypeScreenServices.updateRunType(id, runTypeData);
      if (response.status) {
        toast.success('Run type updated successfully');
        this.fetchRunTypes(); // Refresh the list
      } else {
        toast.error(response.message || 'Failed to update run type');
      }
    } catch (error) {
      toast.error('Error updating run type');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  deleteRunType = async (id: number) => {
    this.isLoading = true;
    try {
      const response = await runTypeScreenServices.deleteRunType(id);
      if (response.status) {
        toast.success('Run type deleted successfully');
        this.fetchRunTypes(); // Refresh the list
      } else {
        toast.error(response.message || 'Failed to delete run type');
      }
    } catch (error) {
      toast.error('Error deleting run type');
    } finally {
      runInAction(() => {
        this.isLoading = false;
      });
    }
  };

  setSelectedRunType = (runType: RunType | null) => {
    this.selectedRunType = runType;
  };
}

export const runTypeStore = new RunTypeStore();
